/**
 * E41 · the portal's side of the Behavioral activation tools — the field lists the data provider asks for, the shapes it gets back,
 * and the pure helpers the hub's Behavioral Activation tab uses. The rules themselves (the ratings 0–10, the plan in the past, who may plan with
 * the client) are the decider's; the hub only shapes what it sends.
 */
export type PlanItem = {
  plannedActivityId: string;
  title: string;
  valueActivityId: string | null;
  plannedFor: string;
  durationMinutes: number | null;
  predictedEnjoyment: number;
  predictedImportance: number;
  certainty: number | null;
  replanOf: string | null;
  assignmentId: string;
  plannedBy: string | null;
  status: 'planned' | 'done' | 'missed' | 'replanned';
  enjoyment: number | null;
  importance: number | null;
  note: string | null;
  barrier: string | null;
};

export type PredictedActual = { predicted: number | null; actual: number | null };

export type WeekRow = {
  subject: string;
  week: string;
  planned: number;
  done: number;
  missed: number;
  predictedVsActual: { enjoyment: PredictedActual; importance: PredictedActual };
  byLifeArea: Array<{ area: string; hours: number }>;
  emptyAreas: string[];
  moodByDay: Array<{ date: string; mood: number | null; activitiesDone: number }>;
  topBarrier: string | null;
  healthActivity: { activeDays: number; activeMinutes: number; walks: number } | null;
  plans: PlanItem[];
  hours: Array<{
    activityLogId: string;
    startAt: string;
    endAt: string;
    title: string;
    enjoyment: number;
    importance: number;
    valueActivityId: string | null;
    area: string | null;
  }>;
  moods: Array<{ date: string; mood: number }>;
  activityDays: Array<{ date: string; activeMinutes: number }>;
};

export type LadderRow = {
  subject: string;
  rankingId: string | null;
  rankedAt: string | null;
  rankedBy: string | null;
  rungs: Array<{
    rank: number;
    valueActivityId: string;
    title: string;
    value: string;
    difficulty: number;
    area: string | null;
  }>;
};

const PLAN_ITEM_FIELDS = [
  'plannedActivityId',
  'title',
  'valueActivityId',
  'plannedFor',
  'durationMinutes',
  'predictedEnjoyment',
  'predictedImportance',
  'certainty',
  'replanOf',
  'assignmentId',
  'plannedBy',
  'status',
  'enjoyment',
  'importance',
  'note',
  'barrier',
];

export const WEEK_FIELDS = [
  'subject',
  'week',
  'planned',
  'done',
  'missed',
  {
    predictedVsActual: [
      { enjoyment: ['predicted', 'actual'] },
      { importance: ['predicted', 'actual'] },
    ],
  },
  { byLifeArea: ['area', 'hours'] },
  'emptyAreas',
  { moodByDay: ['date', 'mood', 'activitiesDone'] },
  'topBarrier',
  { healthActivity: ['activeDays', 'activeMinutes', 'walks'] },
  { plans: PLAN_ITEM_FIELDS },
  {
    hours: [
      'activityLogId',
      'startAt',
      'endAt',
      'title',
      'enjoyment',
      'importance',
      'valueActivityId',
      'area',
    ],
  },
  { moods: ['date', 'mood'] },
  { activityDays: ['date', 'activeMinutes'] },
];

export const LADDER_FIELDS = [
  'subject',
  'rankingId',
  'rankedAt',
  'rankedBy',
  { rungs: ['rank', 'valueActivityId', 'title', 'value', 'difficulty', 'area'] },
];

export const IDEAS_FIELDS = [
  'subject',
  'libraryId',
  { ideas: ['ideaId', 'text', 'area', 'difficulty', 'forYourValue', 'origin'] },
];

export const COMMAND_FIELDS = ['id', 'recorded', 'assignmentId'];

/** The BA steps the Behavioral Activation tab assigns, as `target.type` — each completes on ITS fact (the E34 evidence matcher). */
export const BA_STEPS = [
  'activityMonitoring',
  'activityRanking',
  'activityPlanning',
  'supportContract',
] as const;
export type BaStep = (typeof BA_STEPS)[number];

/** `YYYY-MM-DD` of a Date in the browser's zone. */
export const dayOf = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const mondayOf = (d: Date): Date => {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
  return out;
};

export const addDays = (d: Date, n: number): Date => {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + n);
  return out;
};

const ratingOk = (n: number | null): n is number =>
  n !== null && Number.isInteger(n) && n >= 0 && n <= 10;

export type PlanDraft = {
  title: string;
  plannedFor: Date | null;
  durationMinutes: number | null;
  predictedEnjoyment: number | null;
  predictedImportance: number | null;
  certainty: number | null;
  /** The ladder rung the plan is for (a chip in the Plan drawer sets it; editing "What" afterwards clears it). */
  valueActivityId: string | null;
};

export const emptyPlanDraft = (): PlanDraft => ({
  title: '',
  plannedFor: null,
  durationMinutes: null,
  predictedEnjoyment: null,
  predictedImportance: null,
  certainty: null,
  valueActivityId: null,
});

export const planDraftReady = (d: PlanDraft): boolean =>
  !!d.title.trim() &&
  d.plannedFor !== null &&
  ratingOk(d.predictedEnjoyment) &&
  ratingOk(d.predictedImportance);

/** The in-session plan's input: the server mints the plan's id (the therapist's act has no device queue). */
export const planInput = (d: PlanDraft, subject: string) => ({
  subject,
  title: d.title.trim(),
  plannedFor: (d.plannedFor as Date).toISOString(),
  durationMinutes: d.durationMinutes,
  predictedEnjoyment: d.predictedEnjoyment as number,
  predictedImportance: d.predictedImportance as number,
  certainty: d.certainty,
  valueActivityId: d.valueActivityId,
  replanOf: null,
});

/** A week with nothing in it: no plans, no hours, no mood — the card is the counts line and one "nothing" line. */
export const weekIsEmpty = (v: {
  planned: number;
  done: number;
  missed: number;
  plans: readonly unknown[];
  hours: readonly unknown[];
  moodByDay: ReadonlyArray<{ mood: number | null }>;
}): boolean =>
  v.planned === 0 &&
  v.done === 0 &&
  v.missed === 0 &&
  v.plans.length === 0 &&
  v.hours.length === 0 &&
  v.moodByDay.every((m) => m.mood === null);
